// Sentinel auto-routing state for the workspace: is routing on, which tab of the selected
// brand is shown, how many tickets wait in each brand, and who is online.

import { atom, useAtom, useAtomValue } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';
import { useEffect, useMemo, useState } from 'react';
import { currentUserAtom, isAuthenticatedAtom } from '@/hooks/use-auth';
import { sentinelWorkspaceService } from '@/services/sentinelWorkspace.service';
import type { SentinelUserBrandDto } from '@/modules/sentinel-workspace/types';
import { DEFAULT_MAX_ACTIVE_TICKETS } from '@/modules/sentinel-workspace/brand-team';
import { permissionsAtom } from '@/stores/permissions.store';
import {
  presenceChangesSoFar,
  sentinelPresenceChangesAtom,
  sentinelPresenceOrderAtom,
  websocketStatusAtom,
} from '@/stores/websocket.store';

const RBAC_ENABLED = import.meta.env.VITE_RBAC_ENABLED === 'true';

export type SentinelTicketSection = 'all' | 'unassigned' | 'mine' | 'closed';

export const SENTINEL_SECTIONS: Record<SentinelTicketSection, string> = {
  all: 'All',
  unassigned: 'Unassigned',
  mine: 'Assigned to me',
  closed: 'Closed',
};

/** Every role sees the same four tabs, always for the selected brand. */
export const SECTIONS: readonly SentinelTicketSection[] = ['all', 'unassigned', 'mine', 'closed'];

/** Administrators manage routing and see every queue. Mirrors the backend's administrator check. */
export const canManageSentinelMembersAtom = atom((get) => {
  if (!RBAC_ENABLED) return true;
  const perms = get(permissionsAtom);
  return !!perms && (perms.isSuperAdmin || perms.role === 'administrator');
});

/** Whether routing is on for ANY brand: the workspace shows its tabs as soon as one routes. */
const routingEnabledQueryAtom = atomWithQuery((get) => {
  const user = get(currentUserAtom);
  const accountId = user?.accountId == null ? null : Number(user.accountId);
  return {
    queryKey: ['sentinel', 'routing-enabled', accountId],
    queryFn: () => sentinelWorkspaceService.getRoutingStatus(),
    enabled: get(isAuthenticatedAtom) && accountId !== null,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  };
});

export const routingEnabledAtom = atom((get) => get(routingEnabledQueryAtom).data === true);

/** Only the Sentinel role picks a view; everyone else always sees the brand's All tab. */
export const isSentinelRoleAtom = atom((get) => get(permissionsAtom)?.role === 'sentinel');

/** What the operator picked; null until they pick, which means the default for their role. */
export const selectedSentinelSectionAtom = atom<SentinelTicketSection | null>(null);

/** The tab the ticket list shows, or null for the original queue (routing off everywhere). */
export const resolvedSentinelSectionAtom = atom<SentinelTicketSection | null>((get) => {
  if (!get(routingEnabledAtom)) return null;
  if (!get(isSentinelRoleAtom)) return 'all';
  return get(selectedSentinelSectionAtom) ?? 'mine';
});

/** Unassigned tickets per brand id, for the brand selector. Kept fresh by routing events. */
const unassignedCountsQueryAtom = atomWithQuery((get) => {
  const user = get(currentUserAtom);
  const accountId = user?.accountId == null ? null : Number(user.accountId);
  return {
    queryKey: ['sentinel', 'unassigned-counts', accountId],
    queryFn: () => sentinelWorkspaceService.getUnassignedCounts(),
    enabled: get(isAuthenticatedAtom) && accountId !== null && get(routingEnabledAtom),
    staleTime: 30 * 1000,
  };
});

export function useUnassignedCounts(): Record<string, number> {
  return useAtomValue(unassignedCountsQueryAtom).data ?? {};
}

/** Each user's brands and cap in each, keyed by user id. Administrators only, for the Users page. */
const sentinelUserBrandsQueryAtom = atomWithQuery((get) => {
  const user = get(currentUserAtom);
  const accountId = user?.accountId == null ? null : Number(user.accountId);
  return {
    queryKey: ['sentinel', 'user-brands', accountId],
    queryFn: () => sentinelWorkspaceService.getUserBrands(),
    enabled: get(isAuthenticatedAtom) && accountId !== null && get(canManageSentinelMembersAtom),
  };
});

export function useSentinelUserBrands(): Record<string, SentinelUserBrandDto[]> {
  return useAtomValue(sentinelUserBrandsQueryAtom).data ?? {};
}

/** Each brand's cap for every Sentinel, as it applies. Administrators only, for the Users page. */
const sentinelBrandCapsQueryAtom = atomWithQuery((get) => {
  const user = get(currentUserAtom);
  const accountId = user?.accountId == null ? null : Number(user.accountId);
  return {
    queryKey: ['sentinel', 'brand-caps', accountId],
    queryFn: () => sentinelWorkspaceService.getBrandCaps(),
    enabled: get(isAuthenticatedAtom) && accountId !== null && get(canManageSentinelMembersAtom),
  };
});

/** The brand's cap for every Sentinel, by brand id; null when it has none (or until it loads). */
export function useSentinelBrandCap(): (brandId: string) => number {
  const caps = useAtomValue(sentinelBrandCapsQueryAtom).data;
  return useMemo(() => {
    const byBrand = new Map((caps ?? []).map((cap) => [String(cap.buId), cap.maxActiveTickets]));
    return (brandId: string) => byBrand.get(brandId) ?? DEFAULT_MAX_ACTIVE_TICKETS;
  }, [caps]);
}

export interface SentinelRoutingView {
  routingEnabled: boolean;
  isAdmin: boolean;
  /** Only a Sentinel gets the view dropdown; everyone else is on All. */
  isSentinel: boolean;
  sections: readonly SentinelTicketSection[];
  section: SentinelTicketSection | null;
  setSection: (section: SentinelTicketSection) => void;
}

export function useSentinelRouting(): SentinelRoutingView {
  const routingEnabled = useAtomValue(routingEnabledAtom);
  const isAdmin = useAtomValue(canManageSentinelMembersAtom);
  const isSentinel = useAtomValue(isSentinelRoleAtom);
  const section = useAtomValue(resolvedSentinelSectionAtom);
  const [, setSection] = useAtom(selectedSentinelSectionAtom);
  return { routingEnabled, isAdmin, isSentinel, sections: SECTIONS, section, setSection };
}

/**
 * Who is online in the brand right now: the server's snapshot, with the live changes that
 * arrived after it was asked for applied on top. An older change is already in the snapshot,
 * and applying it again would bring back a state the snapshot corrected (an "online" seen
 * before a missed "offline"). Read again on every reconnect, since changes sent while the
 * socket was down never arrive. For the team table on the Sentinel settings page.
 */
export function useSentinelPresence(buId: string | null): Set<number> {
  const changes = useAtomValue(sentinelPresenceChangesAtom);
  const changeOrder = useAtomValue(sentinelPresenceOrderAtom);
  const socketLive = useAtomValue(websocketStatusAtom) === 'authenticated';
  const [snapshot, setSnapshot] = useState<{ online: number[]; askedAfter: number }>(() => ({
    online: [],
    askedAfter: presenceChangesSoFar(),
  }));

  useEffect(() => {
    const askedAfter = presenceChangesSoFar();
    setSnapshot({ online: [], askedAfter });
    if (!buId) return;
    let cancelled = false;
    void sentinelWorkspaceService
      .getPresence({ bu_id: buId })
      .then((online) => {
        if (!cancelled) setSnapshot({ online, askedAfter });
      })
      .catch(() => {
        // Presence is decoration on this page; the live events still arrive.
      });
    return () => {
      cancelled = true;
    };
  }, [buId, socketLive]);

  return useMemo(() => {
    const online = new Set(snapshot.online);
    for (const [key, isOnline] of Object.entries(changes)) {
      const [changeBuId, userId] = key.split(':');
      if (changeBuId !== buId) continue;
      if ((changeOrder[key] ?? 0) <= snapshot.askedAfter) continue;
      if (isOnline) online.add(Number(userId));
      else online.delete(Number(userId));
    }
    return online;
  }, [snapshot, changes, changeOrder, buId]);
}
